import { useCallback, useEffect, useState } from "react";

import { CustomUserClaims, SignoutOptions, UserClaims } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";

import { logoutPageUrl } from "./routes";

const activeEmployeeGroupRegex = /.*-ActiveEmployees-.*/g;

interface NetJetsCustomUserClaims extends CustomUserClaims {
  readonly groups?: string[];
  readonly domain?: string;
}

const signOutOptions: SignoutOptions = {
  postLogoutRedirectUri: window.location.origin + logoutPageUrl,
  revokeAccessToken: true,
};

export const useAuth = () => {
  const { authState, oktaAuth } = useOktaAuth();

  const [user, setUser] = useState<UserClaims<NetJetsCustomUserClaims>>(
    authState?.idToken?.claims as UserClaims<NetJetsCustomUserClaims>,
  );
  const isAuthenticated = !!authState?.isAuthenticated;

  const login = async () => oktaAuth.signInWithRedirect();
  const logout = async () => oktaAuth.signOut(signOutOptions);
  const isUserInGroup = useCallback((group: string) => user?.groups?.includes(group) || false, [user]);

  const isUserActiveEmployee = user?.groups?.some((group) => group.match(activeEmployeeGroupRegex));

  useEffect(() => {
    const retrieveUser = async () => {
      if (!isAuthenticated) {
        setUser(undefined);
      }
      if (!!isAuthenticated && !user) {
        const result = await oktaAuth.getUser<NetJetsCustomUserClaims>();
        setUser(result);
      }
    };

    retrieveUser();
  }, [isAuthenticated, oktaAuth, user]);

  return {
    isLoading: !authState,
    isAuthenticated,
    user,
    isUserInGroup,
    isUserActiveEmployee,
    login,
    logout,
  };
};
